import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { usePageMeta } from '../hooks/usePageMeta';
import { 
  ArrowRight, 
  CheckCircle, 
  Search,
  TrendingUp,
  Globe,
  Share2,
  Target,
  Palette,
  BarChart,
  Zap,
  Users,
  Award
} from 'lucide-react';

const servicesData = {
  'seo-organic-growth': {
    title: 'SEO & Organic Growth',
    subtitle: 'Dominate search rankings and drive sustainable organic traffic',
    icon: Search,
    heroImage: 'https://images.unsplash.com/photo-1432888498266-38ffec3eaf0a?w=1200',
    problem: {
      title: 'The Challenge',
      description: 'Most businesses struggle to get found online. They\'re buried on page 2 or beyond, while competitors capture all the clicks. Without organic visibility, you\'re leaving money on the table every single day.'
    },
    solution: {
      title: 'Our Approach',
      description: 'We implement a comprehensive SEO strategy that combines technical optimization, content excellence, and authority building to establish your dominance in search results.',
      points: [
        'Technical SEO audit and optimization',
        'Keyword research and content strategy',
        'On-page optimization for every page',
        'Link building and authority development',
        'Local SEO for geo-targeted visibility',
        'Monthly reporting and optimization'
      ]
    },
    outcome: {
      title: 'The Results',
      stats: [
        { value: '4000%', label: 'Revenue Growth' },
        { value: '300+', label: 'Keywords Ranked' },
        { value: '#1', label: 'Page Rankings' }
      ],
      description: 'Our SEO strategies have driven 4000% revenue growth for e-commerce clients and secured #1 rankings for competitive keywords across industries.'
    },
    benefits: [
      { icon: TrendingUp, title: 'Sustainable Growth', description: 'Unlike paid ads, SEO delivers compounding returns over time.' },
      { icon: Target, title: 'High-Intent Traffic', description: 'Attract visitors actively searching for what you offer.' },
      { icon: Award, title: 'Brand Authority', description: 'Top rankings establish trust and credibility.' },
      { icon: BarChart, title: 'Measurable ROI', description: 'Track every lead and conversion from organic search.' }
    ]
  },
  'performance-marketing': {
    title: 'Performance Marketing',
    subtitle: 'ROI-driven campaigns on Meta & Google Ads',
    icon: TrendingUp,
    heroImage: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=1200',
    problem: {
      title: 'The Challenge',
      description: 'Many businesses waste their ad budget on poorly targeted campaigns with low conversion rates. Without data-driven optimization, you\'re essentially throwing money away.'
    },
    solution: {
      title: 'Our Approach',
      description: 'We build high-converting ad campaigns with precise targeting, compelling creative, and continuous optimization to maximize your return on ad spend.',
      points: [
        'Comprehensive audience research and targeting',
        'High-converting ad creative development',
        'Landing page optimization',
        'A/B testing and performance optimization',
        'Retargeting and remarketing strategies',
        'Real-time performance monitoring'
      ]
    },
    outcome: {
      title: 'The Results',
      stats: [
        { value: '344%', label: 'Revenue Increase' },
        { value: '15-18%', label: 'Conversion Rate' },
        { value: '₹0.42', label: 'Avg. CPC' }
      ],
      description: 'We\'ve achieved 344% YoY revenue growth for e-commerce clients and 15-18% conversion rates for education sector campaigns.'
    },
    benefits: [
      { icon: Zap, title: 'Fast Results', description: 'See leads and sales within days of launching campaigns.' },
      { icon: Target, title: 'Precision Targeting', description: 'Reach your ideal customers at the right moment.' },
      { icon: BarChart, title: 'Scalable Growth', description: 'Scale winning campaigns for exponential growth.' },
      { icon: Users, title: 'Full-Funnel Coverage', description: 'From awareness to conversion, we optimize every stage.' }
    ]
  },
  'website-design-development': {
    title: 'Website Design & Development',
    subtitle: 'Conversion-focused websites that drive results',
    icon: Globe,
    heroImage: 'https://images.unsplash.com/photo-1467232004584-a241de8bcf5d?w=1200',
    problem: {
      title: 'The Challenge',
      description: 'Your website is often the first impression potential customers have of your business. A slow, outdated, or poorly designed site drives visitors away and kills conversions.'
    },
    solution: {
      title: 'Our Approach',
      description: 'We build modern, fast-loading websites optimized for conversions. Every element is designed with your business goals in mind.',
      points: [
        'Conversion-focused UX/UI design',
        'Mobile-first responsive development',
        'Speed optimization for fast loading',
        'SEO-friendly architecture',
        'Clear calls-to-action throughout',
        'Analytics and tracking setup'
      ]
    },
    outcome: {
      title: 'The Results',
      stats: [
        { value: '3x', label: 'Conversion Rate' },
        { value: '<2s', label: 'Load Time' },
        { value: '50%', label: 'Bounce Rate Drop' }
      ],
      description: 'Our websites consistently deliver 3x higher conversion rates and significantly reduced bounce rates.'
    },
    benefits: [
      { icon: Zap, title: 'Lightning Fast', description: 'Speed-optimized for better user experience and SEO.' },
      { icon: Target, title: 'Conversion Optimized', description: 'Every element designed to convert visitors.' },
      { icon: Globe, title: 'Mobile-First', description: 'Perfect experience on every device.' },
      { icon: Search, title: 'SEO Ready', description: 'Built with search engines in mind.' }
    ]
  },
  'social-media-marketing': {
    title: 'Social Media Marketing',
    subtitle: 'Build engaged communities and brand awareness',
    icon: Share2,
    heroImage: 'https://images.unsplash.com/photo-1611162616475-46b635cb6868?w=1200',
    problem: {
      title: 'The Challenge',
      description: 'Social media is noisy. Without a clear strategy, your content gets lost in the feed, engagement drops, and your brand becomes invisible to potential customers.'
    },
    solution: {
      title: 'Our Approach',
      description: 'We create content strategies that resonate with your audience, build community, and drive meaningful engagement that translates to business results.',
      points: [
        'Platform-specific content strategy',
        'Engaging content creation',
        'Community management',
        'Influencer partnerships',
        'Social listening and monitoring',
        'Performance analytics and reporting'
      ]
    },
    outcome: {
      title: 'The Results',
      stats: [
        { value: '300%', label: 'Engagement Growth' },
        { value: '50K+', label: 'Followers Gained' },
        { value: '10x', label: 'Reach Increase' }
      ],
      description: 'We help brands build thriving communities that translate into loyal customers and brand advocates.'
    },
    benefits: [
      { icon: Users, title: 'Community Building', description: 'Create engaged followers who become customers.' },
      { icon: Award, title: 'Brand Awareness', description: 'Increase visibility and recognition.' },
      { icon: Target, title: 'Targeted Reach', description: 'Reach your ideal audience organically.' },
      { icon: TrendingUp, title: 'Measurable Growth', description: 'Track engagement, reach, and conversions.' }
    ]
  },
  'conversion-rate-optimization': {
    title: 'Conversion Rate Optimization',
    subtitle: 'Turn more visitors into paying customers',
    icon: Target,
    heroImage: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1200',
    problem: {
      title: 'The Challenge',
      description: 'You\'re getting traffic but not conversions. Visitors browse and leave without taking action. Every lost conversion is lost revenue.'
    },
    solution: {
      title: 'Our Approach',
      description: 'We analyze user behavior, identify conversion barriers, and implement data-driven optimizations to turn more visitors into customers.',
      points: [
        'Comprehensive conversion audit',
        'User behavior analysis (heatmaps, recordings)',
        'A/B and multivariate testing',
        'Landing page optimization',
        'Form and checkout optimization',
        'Ongoing testing and iteration'
      ]
    },
    outcome: {
      title: 'The Results',
      stats: [
        { value: '2-3x', label: 'Conversion Rate' },
        { value: '40%', label: 'Revenue Lift' },
        { value: '-30%', label: 'Cart Abandonment' }
      ],
      description: 'Our CRO strategies typically double or triple conversion rates, directly impacting your bottom line.'
    },
    benefits: [
      { icon: TrendingUp, title: 'More Revenue', description: 'Get more from your existing traffic.' },
      { icon: BarChart, title: 'Data-Driven', description: 'Decisions backed by real user data.' },
      { icon: Zap, title: 'Quick Wins', description: 'See improvements within weeks.' },
      { icon: Target, title: 'Continuous Improvement', description: 'Ongoing testing for constant optimization.' }
    ]
  },
  'branding-strategy': {
    title: 'Branding & Strategy',
    subtitle: 'Create memorable brands that stand out',
    icon: Palette,
    heroImage: 'https://images.unsplash.com/photo-1558655146-9f40138edfeb?w=1200',
    problem: {
      title: 'The Challenge',
      description: 'In a crowded market, generic branding makes you invisible. Without a clear brand identity and strategy, you\'re competing on price alone.'
    },
    solution: {
      title: 'Our Approach',
      description: 'We help you define and communicate a brand that resonates with your target audience and differentiates you from competitors.',
      points: [
        'Brand strategy and positioning',
        'Visual identity design',
        'Brand messaging and voice',
        'Brand guidelines development',
        'Marketing collateral design',
        'Brand implementation support'
      ]
    },
    outcome: {
      title: 'The Results',
      stats: [
        { value: '2x', label: 'Brand Recognition' },
        { value: '40%', label: 'Premium Pricing' },
        { value: '3x', label: 'Customer Loyalty' }
      ],
      description: 'Strong branding enables premium pricing, builds customer loyalty, and creates lasting competitive advantage.'
    },
    benefits: [
      { icon: Award, title: 'Stand Out', description: 'Differentiate from competitors.' },
      { icon: TrendingUp, title: 'Premium Pricing', description: 'Command higher prices with strong brand equity.' },
      { icon: Users, title: 'Customer Loyalty', description: 'Build emotional connections with customers.' },
      { icon: Target, title: 'Clear Messaging', description: 'Communicate your value effectively.' }
    ]
  }
};

export const ServicePage = ({ onOpenAuditModal }) => {
  const { slug } = useParams();
  const service = servicesData[slug];
  usePageMeta(service ? { 
    title: `${service.title} | Digital Startup`,
    description: service.description 
  } : {});

  if (!service) {
    return (
      <main className="pt-20 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-[#1A1A1A] mb-4">Service Not Found</h1>
          <Button asChild>
            <Link to="/">Go Home</Link>
          </Button>
        </div>
      </main>
    );
  }

  const Icon = service.icon;

  return (
    <main className="pt-20" data-testid={`service-page-${slug}`}>
      {/* Hero Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-orange-50 rounded-xl flex items-center justify-center">
                  <Icon className="w-6 h-6 text-[#FFAA17]" />
                </div>
                <p className="text-sm font-medium tracking-wide uppercase text-[#FFAA17]">
                  Our Services
                </p>
              </div>
              <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-[#1A1A1A] mb-4">
                {service.title}
              </h1>
              <p className="text-base md:text-lg text-[#525252] mb-8">
                {service.subtitle}
              </p>
              <div className="flex flex-wrap gap-4">
                <Button
                  onClick={onOpenAuditModal}
                  className="bg-[#FFAA17] hover:bg-[#E69500] text-white rounded-full px-6"
                  data-testid="service-cta-primary"
                >
                  Get Free Growth Audit
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                <Button
                  asChild
                  variant="outline"
                  className="border-[#E5E5E5] text-[#1A1A1A] rounded-full px-6"
                >
                  <Link to="/contact">Contact Us</Link>
                </Button>
              </div>
            </div>
            <div className="relative">
              <img
                src={service.heroImage}
                alt={service.title}
                className="rounded-2xl shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-16 md:py-24 bg-[#F7F7F7]">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-sm font-medium tracking-wide uppercase text-[#FFAA17] mb-3">
              {service.problem.title}
            </p>
            <p className="text-xl md:text-2xl text-[#1A1A1A] leading-relaxed">
              {service.problem.description}
            </p>
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div>
              <p className="text-sm font-medium tracking-wide uppercase text-[#FFAA17] mb-3">
                {service.solution.title}
              </p>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#1A1A1A] mb-6">
                How We Help You Succeed
              </h2>
              <p className="text-[#525252] mb-8">
                {service.solution.description}
              </p>
              <Button
                onClick={onOpenAuditModal}
                className="bg-[#FFAA17] hover:bg-[#E69500] text-white rounded-full px-6"
              >
                Get Started <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
            <div className="bg-[#F7F7F7] rounded-2xl p-8">
              <h3 className="font-semibold text-[#1A1A1A] mb-6">What's Included:</h3>
              <ul className="space-y-4">
                {service.solution.points.map((point, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-[#FFAA17] flex-shrink-0" />
                    <span className="text-[#525252]">{point}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Outcome Section */}
      <section className="py-16 md:py-24 bg-[#2E2E2E]">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center mb-12">
            <p className="text-sm font-medium tracking-wide uppercase text-[#FFAA17] mb-3">
              {service.outcome.title}
            </p>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-white">
              Proven Results That Matter
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            {service.outcome.stats.map((stat, index) => (
              <div key={index} className="text-center">
                <p className="text-4xl md:text-5xl font-bold text-[#FFAA17]">{stat.value}</p>
                <p className="text-gray-400 mt-2">{stat.label}</p>
              </div>
            ))}
          </div>
          <p className="text-gray-300 text-center max-w-2xl mx-auto">
            {service.outcome.description}
          </p>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center mb-12">
            <p className="text-sm font-medium tracking-wide uppercase text-[#FFAA17] mb-3">
              Benefits
            </p>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#1A1A1A]">
              Why Choose This Service
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {service.benefits.map((benefit, index) => {
              const BenefitIcon = benefit.icon;
              return (
                <div
                  key={index}
                  className="bg-[#F7F7F7] rounded-2xl p-6 hover:shadow-md transition-shadow"
                >
                  <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center mb-4 shadow-sm">
                    <BenefitIcon className="w-6 h-6 text-[#FFAA17]" />
                  </div>
                  <h3 className="text-lg font-semibold text-[#1A1A1A] mb-2">{benefit.title}</h3>
                  <p className="text-[#525252] text-sm">{benefit.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-[#FFAA17]">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Ready to Get Started?
          </h2>
          <p className="text-white/80 mb-8 max-w-2xl mx-auto">
            Get a free growth audit and discover how our {service.title.toLowerCase()} services 
            can help grow your business.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={onOpenAuditModal}
              className="bg-white text-[#FFAA17] hover:bg-gray-100 rounded-full px-8 py-3"
            >
              Get Free Growth Audit
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <Button
              asChild
              variant="outline"
              className="border-white text-white hover:bg-white/10 rounded-full px-8 py-3"
            >
              <Link to="/contact">Contact Us</Link>
            </Button>
          </div>
        </div>
      </section>
    </main>
  );
};

export default ServicePage;
